import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './ArticleViewPage.css';

const ArticleViewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [article, setArticle] = useState(null);
  const [content, setContent] = useState(null);

  useEffect(() => {
    const loadArticle = async () => {
      setLoading(true);
      try {
        const metaResp = await fetch(`/api/articles/${id}/content`);
        if (!metaResp.ok) throw new Error('Статья не найдена');
        const meta = await metaResp.json();
        setArticle(meta);
        if (meta.contentJson) setContent(JSON.parse(meta.contentJson));
      } catch (e) {
        setError('Статья не найдена');
        console.error('Ошибка загрузки статьи:', e);
      } finally {
        setLoading(false);
      }
    };

    loadArticle();
  }, [id]);

  // Функция для рендеринга текста с HTML
  const renderTextWithHTML = (text) => {
    return { __html: text };
  };

  // Функция для рендеринга блока
  const renderBlock = (block, index) => {
    switch (block.type) {
      case 'paragraph':
        return (
            <p
                key={index}
                className="content-paragraph"
                dangerouslySetInnerHTML={renderTextWithHTML(block.data.text)}
            />
        );

      case 'header':
        const Tag = `h${block.data.level}`;
        return (
            <Tag key={index} className="content-heading">
              {block.data.text}
            </Tag>
        );

      case 'list':
        const ListTag = block.data.style === 'ordered' ? 'ol' : 'ul';
        return (
            <ListTag key={index} className="content-list">
              {block.data.items?.map((item, idx) => (
                  <li
                      key={idx}
                      dangerouslySetInnerHTML={renderTextWithHTML(item.content || item)}
                  />
              ))}
            </ListTag>
        );

      case 'image':
        return (
            <figure key={index} className="content-image">
              <img src={block.data.file?.url} alt={block.data.caption || ''} />
              {block.data.caption && <figcaption>{block.data.caption}</figcaption>}
            </figure>
        );

      case 'quote':
        return (
            <blockquote key={index} className="content-quote">
              <div dangerouslySetInnerHTML={renderTextWithHTML(block.data.text)} />
              {block.data.caption && <cite>— {block.data.caption}</cite>}
            </blockquote>
        );

      case 'embed':
        return (
            <div
                key={index}
                className="content-embed"
                dangerouslySetInnerHTML={renderTextWithHTML(block.data.embed)}
            />
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
        <div className="article-container">
          <div className="article-skeleton">
            <div className="skeleton-back-button"></div>
            <div className="skeleton-title"></div>
            <div className="skeleton-meta"></div>
            <div className="skeleton-image"></div>
            <div className="skeleton-content">
              <div className="skeleton-text-line"></div>
              <div className="skeleton-text-line"></div>
              <div className="skeleton-text-line"></div>
              <div className="skeleton-text-line"></div>
            </div>
          </div>
        </div>
    );
  }

  if (error) {
    return (
        <div className="article-container">
          <div className="article-error">
            <div className="error-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="#E53E3E" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <h2>Произошла ошибка</h2>
            <p>{error}</p>
            <button className="back-button" onClick={() => navigate(-1)}>
              Вернуться назад
            </button>
          </div>
        </div>
    );
  }

  if (!article) return null;

  return (
      <div className="article-container">
        <article className="article-content">
          <button className="back-button" onClick={() => navigate(-1)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Назад к ленте
          </button>

          <header className="article-header">
            <h1>{article.name}</h1>
            <div className="article-meta">
              {article.isActual && <span className="actual-badge">Актуально</span>}
              <span className="publish-date">
                {new Date(article.createdAt || article.updatedAt).toLocaleDateString('ru-RU')}
              </span>
              <span className="read-time">5 мин чтения</span>
            </div>
          </header>

          {article.coverImageUrl && (
              <div className="article-image">
                <img
                    src={article.coverImageUrl}
                    alt={article.name}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                />
              </div>
          )}

          {article.description && (
              <p className="article-description">{article.description}</p>
          )}

          <div className="article-body">
            {content?.blocks?.map((block, index) => renderBlock(block, index))}
          </div>

          {article.source && (
              <footer className="article-footer">
                <div className="article-source">
                  <strong>Источник:</strong>
                  <a href={article.source} target="_blank" rel="noopener noreferrer">
                    {article.source}
                  </a>
                </div>
              </footer>
          )}
        </article>
      </div>
  );
};

export default ArticleViewPage;